import _ from 'lodash';

import products from '../../services/products/productHandler';
import idGenerator from './idGenerator';
import getDefaultPac from './getDefaultPAC';
import tacHelpers from '../../state/tac/tacHelpers';
import productService from '../../services/products';
import geometry from '../../scene/util/geometry';
import getWallPoints from '../../state/tac/tacReducer/getWallPoints';
import constants from '../../settings/constants';
import { floor } from '../round';
import updateDependentItems from '../../state/tac/tacReducer/updateDependentItems';
import { applicationSettings } from '../../settings/application';
import platform from '../platform';
import { RANGES } from '../../constants';

function parseItems(items) {
  const isElvarli = applicationSettings.applicationName === RANGES.ELVARLI;
  return items.map(item => {
    const product = products.find(product => product.id === item.id);
    let parsedItem = {};

    Object.assign(parsedItem, product);
    parsedItem = {
      ...parsedItem,
      itemid: idGenerator.id(),
      id: item.id,
      ...(isElvarli && item.height && { height: item.height }),
      x: item.x,
      y: item.y,
      z: item.z || 0,
    };

    if (item.items) {
      parsedItem.items = parseItems(item.items);
    }
    return parsedItem;
  });
}

/**
 * Gets the items with only the props relevant for the PAC left
 * @param {*} items
 * @returns {Array}
 */
function compressItems(items) {
  return items.map(({ id, itemid, x, y, z, height, items: itemsItems }) => ({
    id,
    itemid,
    x,
    y,
    z,
    height,
    ...(itemsItems && { items: compressItems(itemsItems) }),
  }));
}

function resetX(items) {
  const minX = Math.min(...items.map(item => item.x));
  return items.map(item => ({ ...item, x: item.x - minX }));
}

function updateSidewalls(tac, wallWidth) {
  const rightSidewall = tac.items.find(
    item => item.x > 0 && productService.isType(item, 'sidewall')
  );

  if (rightSidewall) {
    rightSidewall.x = wallWidth;
  }
}

function adjustWallAgnosticTac(tac) {
  const getSizeBasedOnPoints = points => {
    const { height, width } = geometry.surround(points);
    return { height, width };
  };

  const FREE_SPACE = 650; // we want these tacs to load with at least 65cm of free space to each side
  let wallSize = geometry.surround(tac.wall.points);

  const lockedLeft = tac.items.find(
    item =>
      productService.isType(item, 'sidewall') &&
      tacHelpers.hasExtClothesRail(item)
  );

  let rightSidewall = tac.items.find(
    item => item.x > 0 && productService.isType(item, 'sidewall')
  );
  const allSections = tacHelpers.getSections(tac).sort((a, b) => b.x - a.x);
  const rightmostSection = allSections[0];
  // normally we would just do tacHelpers.isClothesRailConnected() on the right sidewall,
  // but here we don't have that information yet
  const lockedRight =
    rightSidewall && tacHelpers.hasExtClothesRail(rightmostSection);

  if (lockedLeft && lockedRight) {
    const points = getWallPoints(
      tac.wall.points,
      rightSidewall.x,
      wallSize.height
    );
    // tac goes wall-to-wall, adjust wall according to position of the right sidewall
    tac.wall = {
      points,
      size: getSizeBasedOnPoints(points),
    };
    return;
  }

  const size = tacHelpers.getSize(tac);
  const limits = tacHelpers.getLimits(tac);

  if (lockedLeft) {
    // tac is connected to only the left sidewall so move nothing,
    // but make sure there is some extra space on the right

    const actualWidth = limits.max.x;

    const newWidth = Math.max(wallSize.width, actualWidth + FREE_SPACE);
    if (newWidth !== wallSize.width) {
      const points = getWallPoints(
        tac.wall.points,
        size.width + FREE_SPACE,
        wallSize.height
      );
      tac.wall = {
        points,
        size: getSizeBasedOnPoints(points),
      };
      updateSidewalls(tac, newWidth);
    }
    return;
  }

  const minX = Math.min(
    ...tac.items
      .filter(item => !productService.isType(item, 'sidewall'))
      .map(item => item.x)
  );

  if (lockedRight) {
    // tac is connected to only the right sidewall
    const actualWidth = rightSidewall.x - limits.min.x;

    let move;
    if (actualWidth + FREE_SPACE > wallSize.width) {
      // move stuff to create some space on the left
      move = minX - FREE_SPACE;
    } else {
      // just move everything to the right end of the wall
      move = -(wallSize.width - rightSidewall.x);
    }

    tac.items = tac.items.map(item => ({
      ...item,
      x: item.x === 0 ? 0 : item.x - move,
    }));

    rightSidewall = tac.items.find(
      item => item.x > 0 && productService.isType(item, 'sidewall')
    );
    const points = getWallPoints(
      tac.wall.points,
      rightSidewall.x,
      wallSize.height
    );
    tac.wall = {
      points,
      size: getSizeBasedOnPoints(points),
    };
    return;
  }

  // if we reach here, this is a "free-standing" spr, not connected to any walls

  // let's first make sure that the wall is big enough
  const newWidth = Math.max(wallSize.width, size.width + FREE_SPACE * 2);
  if (newWidth !== wallSize.width) {
    const points = getWallPoints(tac.wall.points, newWidth, wallSize.height);
    tac.wall = {
      points,
      size: getSizeBasedOnPoints(points),
    };
    wallSize = geometry.surround(tac.wall.points);
  }

  // and then center the configuration
  const move = minX - floor((wallSize.width - size.width) / 2, 1);
  tac.items = tac.items.map(item => ({
    ...item,
    x: item.x === 0 ? 0 : item.x - move,
  }));

  updateSidewalls(tac, newWidth);
}

/**
 * Checks whether a conversion from PAC to TAC is correct according to range specific rules
 * @param {*} pac
 * @param {*} tac
 * @returns {Boolean}
 */
const isInvalidConversion = (pac, tac) => {
  switch (applicationSettings.applicationName) {
    case 'BOAXEL':
      if (tac.items.length) {
        const wallRect = {
          ...geometry.surround(tac.wall.points),
          depth: constants.ROOM_DEPTH,
        };
        const tacRect = geometry.surround(tac.items);

        if (!geometry.contains(wallRect, tacRect)) {
          return true;
        }
      }
      break;
    default:
      break;
  }

  return false;
};

/**
 * Gets the settings from a PAC, stripped of outdated properties
 * @param {*} pac
 * @returns {Object}
 */
const getPacSettings = pac => {
  if (!pac.settings) {
    return {};
  }
  const { filter, ...settingsWithoutFilter } = pac.settings;
  return settingsWithoutFilter;
};

/**
 * Gets a new version of the TAC, with the wall points set to their max values
 * @param {*} tac
 * @returns {Object}
 */
const getMaxWalledTac = tac => {
  const points = getWallPoints(
    tac.wall.points,
    constants.WALL.width.max,
    constants.WALL.height.max
  );
  const { width, height } = geometry.surround(points);
  return {
    ...tac,
    wall: {
      ...tac.wall,
      points,
      size: {
        width,
        height,
      },
    },
  };
};

/**
 * Gets a new version of the TAC, with range specific adjustments made
 * @param {*} pac
 * @param {*} tac
 * @returns {Object|undefined}
 */
const getRangeAdjustedTac = (pac, tac) => {
  let newTac = { ...tac };
  switch (applicationSettings.applicationName) {
    case 'BOAXEL':
      if (isInvalidConversion(pac, newTac)) {
        newTac = getMaxWalledTac(newTac);
        newTac = updateDependentItems(newTac, { isPersistent: true });

        if (isInvalidConversion(pac, newTac)) {
          return;
        }
      }
      return newTac;
    default:
      return newTac;
  }
};

/**
 * Gets a new version of the TAC with flags set for which (if any) toasts to display on load
 * @param {*} tac
 * @param {*} pac
 * @returns {Object}
 */
export const getToastFlaggedTac = (tac, pac, extraItemsExist) => {
  const getArticlesExchangedToast = () => {
    const articlesExchangedForBetterSuitedOption = !tacHelpers.itemsUnchanged(
      tac.items,
      pac.model.items,
      {
        props: ['id', 'items'],
        realArticles: true,
      }
    );
    return (
      articlesExchangedForBetterSuitedOption && ['TOASTER_ARTICLE_EXCHANGED']
    );
  };

  const getResetWallToast = () => {
    const configurationNoLongerFitsInTheRoom =
      pac.wall && tac.wall && !_.isEqual(pac.wall, tac.wall);
    return configurationNoLongerFitsInTheRoom && ['TOASTER_RESET_WALL'];
  };

  const getExtraItemsToast = () => {
    return (
      extraItemsExist && [
        platform.isKiosk ? 'TOASTER_PEGBOARD_KIOSK' : 'TOASTER_PEGBOARD_INFO',
      ]
    );
  };

  return [
    getExtraItemsToast(),
    getResetWallToast(),
    getArticlesExchangedToast(),
  ].filter(Boolean);
};

/**
 * Converts a PAC (Persistent Article Configuration, i.e. what is saved in VPC/save game)
 * to a TAC (Temporary Article Configuration, i.e. what the planner uses in Redux)
 * @param {*} pac
 * @returns {Object}
 */
function PACtoTAC(pac) {
  const rawItems = parseItems(pac.model.items);
  const defaultPAC = getDefaultPac();
  const wall = pac.wall || defaultPAC.wall;
  let tac = {
    items: rawItems,
    settings: { ...defaultPAC.settings, ...getPacSettings(pac) },
    ...(defaultPAC.wall && { wall }),
  };

  if (pac.wall && !pac.wall.size) {
    /* Backward compatibility for PACs without the size object in their schema.
       Old VPC codes, in particular, can lack the size object. */
    tac.wall.size = {
      width: pac.wall.points?.[2]?.x || constants.WALL.width.max,
      height: pac.wall.points?.[2]?.y || constants.WALL.height.max,
    };
  }

  if (defaultPAC.wall && !pac.wall) {
    //this is wall-less PAC in a wall-full range, probably an SPR
    adjustWallAgnosticTac(tac);
    tac = updateDependentItems(tac, { isPersistent: true });
    adjustWallAgnosticTac(tac);
  } else {
    tac = updateDependentItems(tac, { isPersistent: true });
  }

  tac = getRangeAdjustedTac(pac, tac);

  if (!tac) {
    return null;
  }

  return tac;
}

function TACtoPAC(tac) {
  const defaultPAC = getDefaultPac();
  return {
    model: {
      items: compressItems(tac.items),
    },
    version: defaultPAC.version,
    settings: Object.assign({}, defaultPAC.settings, tac.settings),
    wall: tac.wall,
  };
}

export default {
  PACtoTAC,
  TACtoPAC,
  resetX,
  getToastFlaggedTac,
};
